import { z } from "zod";
import type { FabExpansionMode } from "../../../types/GlobalContext";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

/** Tab ids used by the merged transports preferences MiniModal. */
export type TransportsPrefsTabId = "bike" | "car" | "transit" | "rental";

/** Zod schema for floating-action burst UI state. */
const FabUiStateSchema = z.object( {
    "fabExpansionMode": z.enum( [ "arc", "line" ] ).catch( "arc" ).
        default( "arc" ),
    "hasSeenFabBurst":        z.boolean().default( false ),
    "lastTransportsPrefsTab": z.enum( [ "bike", "car", "transit", "rental" ] ).catch( "bike" ).
        default( "bike" ),
    "debugItineraries": z.boolean().default( false ),
} );

/** FAB expansion layout, onboarding flag, and transports modal tab memory. */
export type FabUiState = {
    "fabExpansionMode":       FabExpansionMode;
    "hasSeenFabBurst":        boolean;
    "lastTransportsPrefsTab": TransportsPrefsTabId;
    "debugItineraries":       boolean;
};

/** Parsed defaults for FAB UI slice. */
export const FAB_UI_DEFAULT: FabUiState = FabUiStateSchema.parse( {} ) as FabUiState;

/**
 * Persisted slice for FAB burst layout (lazy-loaded on first use).
 *
 * @remarks Not in `EAGER_PERSISTED_SLICES`; hydrates when a FAB hook mounts.
 */
export const fabUiSlice = definePersistedSlice<FabUiState>( {
    "key":            "fabUi",
    "currentVersion": 3,
    "storage":        "preferences",
    "schema":         FabUiStateSchema,
    "default":        () => FAB_UI_DEFAULT,
    "migrations":     {
        "2": ( previous: unknown ): unknown => {
            if ( previous !== null && typeof previous === "object" ) {
                return {
                    ...previous,
                    "lastTransportsPrefsTab": "bike",
                };
            }

            return previous;
        },
        "3": ( previous: unknown ): unknown => {
            if ( previous !== null && typeof previous === "object" ) {
                return {
                    ...previous,
                    "debugItineraries": false,
                };
            }

            return previous;
        },
    },
} );
